/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo } from "react";
import { Text, View } from "@react-pdf/renderer";
import { getDisplayTitleForPDFExport } from "@utils/stringUtils";
import { CompetenceActivityClass, EvidencePart, LearningObjective, ProgramData } from "@interfaces/Interfaces";
import Collapsible from "react-collapsible";
import {
  Button_Checkbox_Active,
  Button_Checkbox_Inactive,
  Button_Collapse,
  Button_Expand,
} from "@assets/images";
import { IonItem } from "@ionic/react";
import { useState } from "react";
import _ from "lodash";
import classNames from "classnames";
import { PROGRAM_CONSTANTS, PROGRAM_IDS } from "@constants/programs";

type Props = {
  definition: EvidencePart;
  onValueChanged?: (value: any) => void;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue: {
    selectedOutcomes: { id: string; name: string; type?: string }[];
  };
  program?: ProgramData;
  learningOutcomes: LearningObjective[];
  progressCheckCompetence: CompetenceActivityClass;
  currentProgressCheckId: string;
  preselectedLearningObjective?: LearningObjective | null;
};

const getPreselectedLearningObjective = (
  preselectedLearningObjective: LearningObjective | undefined | null
): { id: string; name: string; type?: string }[] | null => {
  if (!preselectedLearningObjective) return null;
  return [{ id: preselectedLearningObjective.ID, name: preselectedLearningObjective.Name, type: "manual" }];
};

const getMaxLearningObjectivesSelectable = (definition: EvidencePart, programID: string): number | undefined => {
  if (definition["Max value"]) {
    const maxValue = definition["Max value"];

    if (isNaN(Number(maxValue))) {
      return undefined;
    }

    return Number(maxValue);
  }

  if (programID === PROGRAM_IDS.SWAST_SP_AP_RP) {
    return PROGRAM_CONSTANTS.SWAST_SP_AP_RP.MAX_CAPABILITIES_SELECTABLE;
  }

  return undefined;
};

const NQPLearningObjectivesMultiSelect: React.FC<Props> = (props) => {
  const preselectedLearningObjective: { id: string; name: string; type?: string }[] | null = useMemo(
    () => getPreselectedLearningObjective(props.preselectedLearningObjective),
    [props.preselectedLearningObjective]
  );

  const maxLearningObjectivesSelectable = useMemo(
    () => getMaxLearningObjectivesSelectable(props.definition, props.program?.ID ?? ""),
    [props.program?.ID]
  );

  const [showDescription, setShowDescription] = useState<boolean>(true);
  const [collapsibleOpen, setCollapsibleOpen] = useState<string[]>([]);
  const [selectedLearningObjectives, setSelectedLearningObjectives] = useState<
    { id: string; name: string; type?: string }[]
  >(preselectedLearningObjective ?? props.initialValue?.selectedOutcomes ?? []);

  useEffect(() => {
    if (preselectedLearningObjective) {
      setSelectedLearningObjectives(preselectedLearningObjective);
      props.onValueChanged?.({ selectedOutcomes: preselectedLearningObjective });
    }
  }, [preselectedLearningObjective]);

  const groupedLearningOutcomes = useMemo(() => {
    const grouped = _.groupBy(props.learningOutcomes, (item) => item.TopSection!);

    if (props.program?.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
      let sortedKeys = [];

      sortedKeys = Object.keys(grouped).sort((a, b) => {
        if (a.toLowerCase().includes("education")) {
          return 1;
        }
        return a.localeCompare(b);
      });

      const sortedGroupedLearningOutcomes: any = {};

      sortedKeys.forEach((key) => {
        sortedGroupedLearningOutcomes[key] = grouped[key];
      });

      return sortedGroupedLearningOutcomes;
    } else {
      return grouped;
    }
  }, [props.learningOutcomes, props.program?.ID]);

  const hasDescriptions = useMemo(() => {
    return props.learningOutcomes.some((item) => item.SubTitle);
  }, [props.learningOutcomes]);

  const mappedLearningOutcomes = useMemo(
    () =>
      Object.keys(groupedLearningOutcomes).map((key) => {
        let object;

        const grouped = _.groupBy(groupedLearningOutcomes[key]!, (item) => item.Section!);

        if (props.program?.ID === PROGRAM_IDS.SWAST_SP_AP_RP) {
          object = { [key]: grouped };
        } else {
          object = { [key]: grouped };
        }

        return object;
      }),
    [groupedLearningOutcomes, props.program?.ID]
  );

  if (props.isPDF) {
    return (
      <View>
        {props.data.selectedOutcomes?.length > 0 && (
          <View>
            <Text style={props.pdfStyles.reflectionHeader}>{getDisplayTitleForPDFExport(props.definition)}</Text>
            {props.data.selectedOutcomes.flat().map((item: { id: string; name: string }) => {
              const learningObjective = props.learningOutcomes.find((lo) => lo.ID === item.id);

              return (
                <View
                  key={item.id}
                  style={{ marginTop: 8 }}
                >
                  <Text style={props.pdfStyles.boldText}>{learningObjective?.Name}</Text>
                  <Text style={props.pdfStyles.normalText}>{learningObjective?.TopSection}</Text>
                  <Text style={props.pdfStyles.normalText}>{learningObjective?.Section}</Text>
                  <Text style={props.pdfStyles.normalText}>{learningObjective?.Description}</Text>
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  }

  if (props.isDisplay) {
    return (
      <div>
        {props.data.selectedOutcomes?.length > 0 && (
          <div className="readEvidenceBlock">
            <div className="readEvidenceHeader mb-2">{props.definition["Display Title"] || props.definition.Name}</div>
            {props.data.selectedOutcomes.map((item: { id: string; name: string }, index: number) => {
              const learningObjective = props.learningOutcomes.find((lo) => lo.ID === item.id);

              return (
                <div
                  key={item.id}
                  className={`${index !== props.data.selectedOutcomes.length - 1 ? "mb-2" : ""}`}
                >
                  <div className="readEvidenceText font-acc-600">{learningObjective?.Name}</div>
                  <div className="readEvidenceText">{learningObjective?.Section}</div>
                  <div className="readEvidenceText">{learningObjective?.Description}</div>
                </div>
              );
            })}
            <div className="h-[1px] w-full bg-grey-30 mt-4" />
          </div>
        )}
      </div>
    );
  }

  const toggleLearningObjective = (learningObjective: LearningObjective): void => {
    const array = _.cloneDeep(selectedLearningObjectives);
    const index = array.findIndex((item) => item.id === learningObjective.ID);

    if (index > -1) {
      array.splice(index, 1);
    } else {
      if (maxLearningObjectivesSelectable && array.length >= maxLearningObjectivesSelectable) {
        window.alert(`You can only select a maximum of ${maxLearningObjectivesSelectable}`);
        return;
      }

      array.push({ id: learningObjective.ID, name: learningObjective.Name, type: "manual" });
    }

    const value = {
      selectedOutcomes: [...array],
    };

    props.onValueChanged?.(value);

    setSelectedLearningObjectives(array);
  };

  const handleCollapsibleToggle = (id: string, close?: boolean): void => {
    const array = _.cloneDeep(collapsibleOpen);
    const index = array.findIndex((item) => item === id);

    if (index > -1) {
      array.splice(index, 1);
    } else {
      !close && array.push(id);
    }

    setCollapsibleOpen(array);
  };

  return (
    <div>
      <div className="reflectionContentCard bg-grey-20">
        <div className="reflectionContentCardTitle !mb-0">
          {props.definition["Display Title"] ?? "Tick the LOs met"}
          <span className="text-cta-red">
            {(props.definition.options === "Mandatory" || props.definition["option type"] === "Mandatory") &&
              !props.definition["Hide Mandatory tag"] &&
              " (Mandatory)"}
          </span>
        </div>
      </div>
      {mappedLearningOutcomes.map((item, mainIndex) => {
        const topSectionKey = Object.keys(item)[0];
        const topSection = item[topSectionKey];

        const object = Object.keys(topSection).map((key) => topSection[key]);
        const sections: LearningObjective[][] = object.filter((section) => section !== undefined);
        const flattenedSectionIDs = sections.flat().map((section) => section.ID);

        const open = collapsibleOpen.includes(topSectionKey);

        const hasLOsSelected =
          selectedLearningObjectives.filter((lo) => flattenedSectionIDs.includes(lo.id)).length > 0;

        return (
          <div
            key={topSectionKey}
            className={classNames("rounded-md border-[1px] border-solid border-grey-40 bg-white overflow-hidden", {
              "mb-4": mainIndex !== mappedLearningOutcomes.length - 1,
            })}
          >
            <Collapsible
              key={topSectionKey}
              trigger={
                <IonItem
                  button
                  detail={false}
                  className="evidencePartsCheckboxContainer"
                  onClick={() => handleCollapsibleToggle(topSectionKey)}
                >
                  <div className="w-full flex flex-row justify-between items-center px-3.5 py-[5px]">
                    <div className="text-17px font-acc-600 leading-default tracking-default text-grey-90">
                      {topSectionKey}
                    </div>
                    <div className="flex flex-row">
                      <img
                        alt=""
                        className="h-icon w-icon img-no-select"
                        src={open ? Button_Collapse : Button_Expand}
                      />
                      <img
                        alt=""
                        className="h-icon w-icon img-no-select"
                        src={hasLOsSelected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                      />
                    </div>
                  </div>
                </IonItem>
              }
              transitionTime={250}
              transitionCloseTime={100}
              open={open}
            >
              <div className="px-4">
                {sections.map((section) => {
                  return (
                    <div key={section[0].Section}>
                      {section.map((learningOutcome, index) => {
                        const selected =
                          selectedLearningObjectives.findIndex((item) => item.id === learningOutcome.ID) > -1;

                        return (
                          <div key={learningOutcome.ID}>
                            {index === 0 && (
                              <>
                                <div className="h-[2px] bg-grey-20" />
                                {hasDescriptions && (
                                  <div className="mt-3 mb-1">
                                    <button onClick={() => setShowDescription(!showDescription)}>
                                      <div className="text-acc-17px leading-default tracking-[-0.3] text-cta-blue">
                                        {showDescription ? "Hide descriptions" : "Show descriptions"}
                                      </div>
                                    </button>
                                  </div>
                                )}
                                {learningOutcome.Section && (
                                  <div className="mt-4 mb-6 text-[12px] font-acc-600 text-grey-90">
                                    {learningOutcome.Section}
                                  </div>
                                )}
                              </>
                            )}
                            <IonItem
                              button
                              detail={false}
                              className="evidencePartsCheckboxContainer mb-3"
                              onClick={() => toggleLearningObjective(learningOutcome)}
                            >
                              <div className="w-full flex flex-col">
                                <div className="w-full flex flex-row justify-between items-center">
                                  <div
                                    className={classNames("text-[12px] font-acc-400 text-grey-90", {
                                      "text-acc-15px font-semibold": !!learningOutcome.SubTitle,
                                    })}
                                  >
                                    {learningOutcome.Description}
                                  </div>
                                  <img
                                    alt=""
                                    className="h-icon w-icon img-no-select"
                                    src={selected ? Button_Checkbox_Active : Button_Checkbox_Inactive}
                                  />
                                </div>
                                {learningOutcome.SubTitle && showDescription && (
                                  <div className="text-acc-12px font-acc-normal text-grey-90 tracking-[-0.1] leading-default mr-4 w-full whitespace-pre-wrap">
                                    {learningOutcome.SubTitle}
                                  </div>
                                )}
                              </div>
                            </IonItem>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
};

export default NQPLearningObjectivesMultiSelect;
